import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";

axios.defaults.withCredentials = true;
const API = process.env.REACT_APP_BACKEND_URL;

/**
 * WebSocket hook for real-time call monitoring.
 * Fetches a short-lived WS token from /api/call-monitor/ws-token,
 * then connects to /api/call-monitor/ws with that token.
 */
export default function useCallMonitorWS() {
  const [connected, setConnected] = useState(false);
  const [activeCalls, setActiveCalls] = useState([]);
  const [transcripts, setTranscripts] = useState({}); // keyed by attemptId
  const [callStatuses, setCallStatuses] = useState({}); // keyed by attemptId
  const wsRef = useRef(null);
  const reconnectTimer = useRef(null);
  const reconnectAttempt = useRef(0);
  const maxReconnect = 10;

  const connect = useCallback(async () => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) return;

    // Fetch a short-lived WS token from the backend (uses HttpOnly session cookie)
    let token = "";
    try {
      const res = await axios.get(`${API}/api/call-monitor/ws-token`);
      token = res.data.token;
    } catch (err) {
      console.error("Failed to get WS token:", err);
      // Retry with backoff
      if (reconnectAttempt.current < maxReconnect) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempt.current), 30000);
        reconnectAttempt.current += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      }
      return;
    }

    if (!API) {
      console.error("REACT_APP_BACKEND_URL is not set — cannot open call-monitor WebSocket");
      return;
    }
    const wsBase = API.replace(/^http/, "ws");
    const url = `${wsBase}/api/call-monitor/ws?token=${token}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onopen = () => {
      setConnected(true);
      reconnectAttempt.current = 0;
      // Auto-subscribe to dashboard updates
      ws.send(JSON.stringify({ type: "subscribe_dashboard" }));
    };

    ws.onmessage = (event) => {
      try {
        const msg = JSON.parse(event.data);

        switch (msg.type) {
          case "active_calls_update":
            // Trigger a re-fetch of active calls — handled by the component
            setActiveCalls((prev) => {
              if (msg.action === "call_ended") {
                return prev.filter((c) => c.attempt_id !== msg.attempt_id);
              }
              // For call_started or call_answered, the component will re-fetch
              return prev;
            });
            break;

          case "transcript_update":
            setTranscripts((prev) => ({
              ...prev,
              [msg.attempt_id]: [
                ...(prev[msg.attempt_id] || []),
                {
                  role: msg.role,
                  text: msg.text,
                  timestamp: msg.timestamp,
                },
              ],
            }));
            break;

          case "call_status":
            setCallStatuses((prev) => ({
              ...prev,
              [msg.attempt_id]: {
                status: msg.status,
                ended: msg.ended || false,
                duration: msg.duration,
              },
            }));
            break;

          case "snapshot":
            if (msg.data) {
              const d = msg.data;
              if (d.transcript) {
                setTranscripts((prev) => ({
                  ...prev,
                  [d.attempt_id]: d.transcript,
                }));
              }
            }
            break;

          case "whisper_applied":
            // Can be handled by the component directly
            break;

          case "barge_status":
            setCallStatuses((prev) => ({
              ...prev,
              [msg.attempt_id]: {
                ...(prev[msg.attempt_id] || {}),
                barge_active: msg.active,
              },
            }));
            break;

          case "pong":
            break;

          default:
            break;
        }
      } catch (e) {
        console.error("WS message parse error:", e);
      }
    };

    ws.onclose = () => {
      setConnected(false);
      wsRef.current = null;
      // Exponential backoff reconnect
      if (reconnectAttempt.current < maxReconnect) {
        const delay = Math.min(1000 * Math.pow(2, reconnectAttempt.current), 30000);
        reconnectAttempt.current += 1;
        reconnectTimer.current = setTimeout(connect, delay);
      }
    };

    ws.onerror = () => {
      ws.close();
    };
  }, []);

  const disconnect = useCallback(() => {
    if (reconnectTimer.current) {
      clearTimeout(reconnectTimer.current);
      reconnectTimer.current = null;
    }
    reconnectAttempt.current = maxReconnect; // prevent reconnect
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setConnected(false);
  }, []);

  const subscribe = useCallback((attemptId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "subscribe", attempt_id: attemptId }));
    }
  }, []);

  const unsubscribe = useCallback((attemptId) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "unsubscribe", attempt_id: attemptId }));
    }
    // Clear transcript for this call
    setTranscripts((prev) => {
      const next = { ...prev };
      delete next[attemptId];
      return next;
    });
  }, []);

  const sendWhisper = useCallback((attemptId, message) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({ type: "whisper", attempt_id: attemptId, message }));
    }
  }, []);

  // Ping to keep alive
  useEffect(() => {
    const interval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ type: "ping" }));
      }
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnect();
    };
  }, [disconnect]);

  return {
    connected,
    activeCalls,
    setActiveCalls,
    transcripts,
    callStatuses,
    connect,
    disconnect,
    subscribe,
    unsubscribe,
    sendWhisper,
  };
}
